<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import Header from './components/Header.svelte';
  import StatsBar from './components/StatsBar.svelte';
  import MapPanel from './components/MapPanel.svelte';
  import DriversList from './components/DriversList.svelte';
  import type { Driver, Stats } from './types';
  import { dispatcherApi, ApiError } from './utils/api';
  import { DispatcherWebSocket } from './utils/websocket';

  // State
  let drivers = $state<Record<string, Driver>>({});
  let stats = $state<Stats>({
    total_conductors: 0,
    total_deliveries: 0,
    completed: 0,
    failed: 0,
    pending: 0,
    success_rate: 0
  });
  let selectedZona = $state('');
  let searchTerm = $state('');
  let zonas = $state<string[]>([]);
  let selectedDriverId = $state<string | null>(null);
  let routeDeliveries = $state<any[]>([]);
  let isLoading = $state(false);
  let error = $state<string | null>(null);
  let connectionStatus = $state<'connecting' | 'connected' | 'disconnected' | 'error'>('disconnected');

  /**
   * Map driver_id -> nonce que se incrementa cada vez que llega un
   * `delivery_status_changed` por WebSocket para ese conductor.
   * DriversList lo baja hasta DriverCard, y si la tarjeta está abierta,
   * dispara un refetch de `/deliveries` para reflejar en tiempo real la
   * entrega que el driver acaba de marcar en la app móvil.
   *
   * Antes solo se actualizaba `stats` (el contador arriba), pero el
   * listado individual quedaba stale hasta colapsar/expandir la tarjeta.
   */
  let driverRefreshNonces = $state<Record<string, number>>({});

  // WebSocket client
  let wsClient: DispatcherWebSocket | null = null;
  let statsInterval: number | null = null;
  let driversInterval: number | null = null;

  onMount(() => {
    initWebSocket();
    loadDashboardData();
    
    // Update stats every 30 seconds as fallback
    statsInterval = setInterval(() => {
      updateStats();
    }, 30000);
    
    // Update drivers every 60 seconds as fallback (WebSocket handles real-time updates)
    driversInterval = setInterval(() => {
      loadAllDrivers(true);
    }, 60000);
  });

  onDestroy(() => {
    if (wsClient) {
      wsClient.disconnect();
    }
    if (statsInterval) {
      clearInterval(statsInterval);
    }
    if (driversInterval) {
      clearInterval(driversInterval);
    }
  });

  async function loadDashboardData() {
    try {
      isLoading = true;
      error = null;

      // Load drivers data
      try {
        const driversResponse = await dispatcherApi.getDrivers(selectedZona);
        
        if ((driversResponse.data as any)?.drivers) {
          const driversData = (driversResponse.data as any).drivers;
          const driversObj: Record<string, Driver> = {};
          
          // Handle both array and object formats
          const driversToProcess = Array.isArray(driversData) ? driversData : Object.values(driversData);
          
          driversToProcess.forEach((driver: any) => {
            // Keep null/undefined coordinates as-is, don't convert to 0
            const lat = driver.lat !== null && driver.lat !== undefined && driver.lat !== 0 ? driver.lat : null;
            const lon = driver.lon !== null && driver.lon !== undefined && driver.lon !== 0 ? driver.lon : null;
            
            // Preserve WebSocket-updated coordinates if more recent than DB
            const existing = drivers[driver.id];
            const useExistingCoords = existing && existing.lat && existing.lon && (existing as any)._ws_updated;
            
            driversObj[driver.id] = {
              name: driver.name,
              lat: useExistingCoords ? existing.lat : lat,
              lon: useExistingCoords ? existing.lon : lon,
              connected: driver.connected || false,
              deliveries_count: driver.deliveries_count || driver.deliveries?.total || 0,
              last_update: useExistingCoords ? existing.last_update : driver.last_update,
              zona: driver.zona,
              available: (useExistingCoords ? existing.lat : lat) !== null
            } as any;
            
            // Debug log for first few drivers
            if (Object.keys(driversObj).length <= 3) {
              console.log(`📍 Driver ${driver.name}: lat=${lat}, lon=${lon}`);
            }
          });

          drivers = driversObj;

          // Extract zones from drivers
          const uniqueZonas = [...new Set(driversToProcess.map((d: any) => d.zona).filter(Boolean))] as string[];
          zonas = uniqueZonas;

          console.log(`✅ Drivers loaded: ${Object.keys(drivers).length} drivers, ${zonas.length} zones`);
        }
      } catch (driversErr) {
        console.warn('⚠️ Error loading drivers:', driversErr);
      }
      
      // Load stats separately from dedicated endpoint - ALWAYS call this
      await updateStats();
      
    } catch (err) {
      if (err instanceof ApiError) {
        error = `API Error (${err.code}): ${err.message}`;
      } else {
        error = err instanceof Error ? err.message : 'Failed to load dashboard data';
      }
      console.error('❌ Error loading dashboard data:', err);
    } finally {
      isLoading = false;
    }
  }

  async function initWebSocket() {
    try {
      connectionStatus = 'connecting';
      
      wsClient = new DispatcherWebSocket();

      // Set up event handlers
      wsClient.on('connection_established', () => {
        connectionStatus = 'connected';
        console.log('✅ WebSocket connection established');
        
        // Register as dispatcher
        wsClient!.registerDispatcher('web_dashboard', 'Web Dashboard');
        
        // Request initial data
        wsClient!.requestAllDrivers(selectedZona);
      });

      wsClient.on('connection_lost', (data) => {
        connectionStatus = 'disconnected';
        console.log('❌ WebSocket connection lost:', data);
      });

      wsClient.on('connection_error', (data) => {
        connectionStatus = 'error';
        console.error('❌ WebSocket connection error:', data);
      });

      wsClient.on('reconnected', (data) => {
        connectionStatus = 'connected';
        console.log('✅ WebSocket reconnected:', data);
        // Re-register and request data
        wsClient!.registerDispatcher('web_dashboard', 'Web Dashboard');
        wsClient!.requestAllDrivers(selectedZona);
      });

      // Handle dispatcher registration success
      wsClient.on('register_dispatcher_success', (data) => {
        console.log('✅ Dispatcher registered successfully:', data);
      });

      // Handle driver location updates
      wsClient.on('driver_location_updated', (data) => {
        console.log('📍 driver_location_updated received:', data);
        
        if (!data || !data.driver_id) {
          console.warn('⚠️ Invalid location update data:', data);
          return;
        }
        
        if (data.lat === undefined || data.lon === null || data.lat === null || data.lon === undefined) {
          console.warn(`⚠️ Invalid coordinates for driver ${data.driver_id}:`, data.lat, data.lon);
          return;
        }
        
        console.log(`📍 Processing location update for driver ${data.driver_id}`);
        console.log(`   Current drivers keys:`, Object.keys(drivers));
        console.log(`   Driver exists in drivers:`, !!drivers[data.driver_id]);
        
        if (drivers[data.driver_id]) {
          // Update existing driver location - trigger reactivity by reassigning the object
          const oldLat = drivers[data.driver_id].lat;
          const oldLon = drivers[data.driver_id].lon;
          
          drivers[data.driver_id] = {
            ...drivers[data.driver_id],
            lat: data.lat,
            lon: data.lon,
            last_update: data.timestamp,
            connected: true,
            available: true,
            _ws_updated: true  // Mark as WebSocket-updated to prevent polling overwrite
          } as any;
          
          console.log(`✅ Updated driver ${data.driver_id} location:`);
          console.log(`   Old: [${oldLat}, ${oldLon}]`);
          console.log(`   New: [${data.lat}, ${data.lon}]`);
          console.log(`   Driver object:`, drivers[data.driver_id]);
        } else {
          console.warn(`⚠️ driver_location_updated: driver_id=${data.driver_id} NOT found in drivers`);
          console.log(`   Available driver IDs:`, Object.keys(drivers));
          console.log(`   Attempting to load driver from API...`);
          
          // Driver not in local state - load it
          loadAllDrivers(true);
        }
      });

      // Handle driver online/offline status
      wsClient.on('driver_online', (data) => {
        console.log('📡 Received driver_online event:', data);
        if (data && data.driver_id) {
          console.log(`✅ Driver ${data.driver_name} (${data.driver_id}) came online`);
          // Always reload drivers list to ensure we have the latest data
          console.log('🔄 Reloading drivers list due to driver_online event');
          loadAllDrivers(true); // silent = true, no loader
        }
      });

      wsClient.on('driver_offline', (data) => {
        if (data && data.driver_id) {
          if (drivers[data.driver_id]) {
            drivers[data.driver_id].connected = false;
          }
          console.log(`❌ Driver ${data.driver_id} went offline`);
        }
      });

      // Handle all drivers data
      wsClient.on('all_drivers_locations', (data) => {
        if (data.drivers && Array.isArray(data.drivers)) {
          data.drivers.forEach((driver: any) => {
            if (drivers[driver.id]) {
              // Reassign object to trigger Svelte 5 reactivity
              drivers[driver.id] = {
                ...drivers[driver.id],
                lat: driver.lat,
                lon: driver.lon,
                last_update: driver.last_update,
                connected: driver.connected
              };
            }
          });
        }
      });

      // Handle delivery status changes
      wsClient.on('delivery_status_changed', (data) => {
        console.log('📦 Delivery status changed:', data);
        // Refresh stats when delivery status changes
        updateStats();

        // Si el evento trae el driver_id, subimos su nonce para que la
        // tarjeta abierta correspondiente recargue el listado sin que
        // el operador tenga que colapsarla y expandirla.
        const affectedDriverId = data?.driver_id ? String(data.driver_id) : null;
        if (affectedDriverId) {
          driverRefreshNonces = {
            ...driverRefreshNonces,
            [affectedDriverId]: (driverRefreshNonces[affectedDriverId] || 0) + 1,
          };
        } else {
          // Sin driver_id no podemos apuntar a una tarjeta específica;
          // subimos el nonce de todas para que las abiertas refresquen.
          const bumped: Record<string, number> = {};
          for (const id of Object.keys(drivers)) {
            bumped[id] = (driverRefreshNonces[id] || 0) + 1;
          }
          driverRefreshNonces = { ...driverRefreshNonces, ...bumped };
        }

        // Ajuste optimista de contadores. /action del driver manda
        // `status` (ENTREGADO/EN SOBRANCIA), no `new_status`, así que
        // aceptamos ambos.
        const s = String(data?.status || data?.new_status || '').toUpperCase();
        if (s === 'ENTREGADO' || s === 'DELIVERED') {
          stats.completed++;
          stats.pending = Math.max(0, stats.pending - 1);
        } else if (s === 'EN SOBRANCIA' || s === 'FAILED' || s === 'EN RETENCION') {
          stats.failed++;
          stats.pending = Math.max(0, stats.pending - 1);
        }
        
        // Recalculate success rate
        const total_completed = stats.completed + stats.failed;
        stats.success_rate = total_completed > 0 
          ? Math.round((stats.completed / total_completed) * 100 * 100) / 100
          : 0;
      });

      // Connect to WebSocket
      await wsClient.connect();
      
    } catch (err) {
      connectionStatus = 'error';
      console.error('❌ Failed to initialize WebSocket:', err);
      error = 'Failed to establish real-time connection';
    }
  }

  async function loadAllDrivers(silent = false) {
    try {
      if (!silent) {
        isLoading = true;
        error = null;
      }

      console.log(`📥 Loading drivers with zona: ${selectedZona || 'all'}`);
      const response = await dispatcherApi.getDrivers(selectedZona);
      
      if ((response.data as any)?.drivers) {
        const driversData = (response.data as any).drivers;
        const driversObj: Record<string, Driver> = {};
        
        // Handle both array and object formats
        const driversToProcess = Array.isArray(driversData) ? driversData : Object.values(driversData);
        
        driversToProcess.forEach((driver: any) => {
          // Keep null/undefined coordinates as-is, don't convert to 0
          const lat = driver.lat !== null && driver.lat !== undefined && driver.lat !== 0 ? driver.lat : null;
          const lon = driver.lon !== null && driver.lon !== undefined && driver.lon !== 0 ? driver.lon : null;
          
          driversObj[driver.id] = {
            name: driver.name,
            lat: lat,
            lon: lon,
            connected: driver.connected || false,
            deliveries_count: driver.deliveries_count || 0,
            last_update: driver.last_update,
            zona: driver.zona,
            available: lat !== null && lon !== null
          };
        });

        drivers = driversObj;
        console.log(`✅ Drivers loaded: ${Object.keys(drivers).length}`);
      }
      
      if (!silent) {
        await updateStats();
      }
    } catch (err) {
      if (!silent) {
        if (err instanceof ApiError) {
          error = `API Error (${err.code}): ${err.message}`;
        } else {
          error = err instanceof Error ? err.message : 'Failed to load drivers';
        }
        console.error('❌ Error loading drivers:', err);
      }
    } finally {
      if (!silent) {
        isLoading = false;
      }
    }
  }

  async function updateStats() {
    try {
      const response = await dispatcherApi.getStats(selectedZona);
      
      if (response.data) {
        const data = response.data as any;
        // Map /statistics response to Stats format
        stats = {
          total_conductors: data.drivers?.total || 0,
          total_deliveries: data.deliveries?.total || 0,
          completed: data.deliveries?.delivered || 0,
          failed: data.deliveries?.failed || 0,
          pending: data.deliveries?.pending || 0,
          success_rate: data.deliveries?.success_rate || 0
        };
        console.log('✅ Stats updated:', stats);
      }
    } catch (err) {
      console.error('❌ Error updating stats:', err);
    }
  }

  function handleZonaChange(zona: string) {
    selectedZona = zona;
    loadAllDrivers();
    updateStats(); // Update stats when zona changes
    // Update WebSocket request
    if (wsClient && wsClient.connected()) {
      wsClient.requestAllDrivers(selectedZona);
    }
  }

  async function handleSearch(term: string) {
    searchTerm = term;
    if (!term.trim()) {
      loadAllDrivers();
      return;
    }

    try {
      // For now, filter locally since search endpoint may not be implemented
      // In a real implementation, you'd call a search API endpoint
      const filteredDrivers: Record<string, Driver> = {};
      Object.entries(drivers).forEach(([id, driver]) => {
        if (driver.name.toLowerCase().includes(term.toLowerCase()) ||
            id.toLowerCase().includes(term.toLowerCase())) {
          filteredDrivers[id] = driver;
        }
      });
      
      // Create a temporary drivers state for search results
      const originalDrivers = drivers;
      drivers = filteredDrivers;
      
      // If search is cleared, restore original drivers
      if (!term.trim()) {
        drivers = originalDrivers;
      }
    } catch (err) {
      console.error('❌ Error in search:', err);
    }
  }

  function handleSelectDriver(driverId: string) {
    if (selectedDriverId === driverId) {
      // Deselecting - clear everything
      selectedDriverId = null;
      routeDeliveries = [];
    } else {
      selectedDriverId = driverId;
    }
  }

  function handleRouteView(driverId: string, route: any[]) {
    console.log('📍 handleRouteView called:', driverId, 'with', route?.length || 0, 'deliveries');
    selectedDriverId = driverId;
    // Show delivery markers directly without route optimization
    routeDeliveries = route || [];
  }
</script>

<div class="fixed inset-0 flex flex-col bg-black overflow-hidden">
  <Header {selectedZona} {zonas} onZonaChange={handleZonaChange} />

  <!-- Connection Status Indicator -->
  {#if connectionStatus !== 'connected'}
    <div class="bg-yellow-500/20 border-b border-yellow-500/30 px-4 py-2 flex-shrink-0">
      <div class="flex items-center gap-2 text-yellow-400 text-sm">
        {#if connectionStatus === 'connecting'}
          <div class="animate-spin w-4 h-4 border-2 border-yellow-400 border-t-transparent rounded-full"></div>
          <span>Connecting to real-time updates...</span>
        {:else if connectionStatus === 'disconnected'}
          <span class="text-red-400">⚠️ Real-time updates disconnected</span>
        {:else if connectionStatus === 'error'}
          <span class="text-red-400">❌ Connection error - using cached data</span>
        {/if}
      </div>
    </div>
  {/if}

  <!-- Error Banner -->
  {#if error}
    <div class="bg-red-500/20 border-b border-red-500/30 px-4 py-2 flex-shrink-0">
      <div class="flex items-center justify-between">
        <span class="text-red-400 text-sm">❌ {error}</span>
        <button 
          onclick={() => error = null}
          class="text-red-400 hover:text-red-300 text-sm"
        >
          ✕
        </button>
      </div>
    </div>
  {/if}

  <StatsBar {stats} />

  <!-- Loading Overlay -->
  {#if isLoading}
    <div class="bg-black/50 border-b border-white/10 px-4 py-2 flex-shrink-0">
      <div class="flex items-center gap-2 text-white text-sm">
        <div class="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
        <span>Actualizando información de conductores...</span>
      </div>
    </div>
  {/if}

  <!-- Seguimiento -->
  <div class="flex-1 flex gap-4 p-4 overflow-hidden min-h-0">
    <MapPanel {drivers} {selectedDriverId} {routeDeliveries} {selectedZona} />
    <DriversList 
      {drivers} 
      {searchTerm} 
      {selectedDriverId}
      onSearch={handleSearch} 
      onSelectDriver={handleSelectDriver}
      onRouteView={handleRouteView}
      {driverRefreshNonces}
    />
  </div>
</div>
